// Layers panel. Legacy: updateLayers/selectLayer (snapshot L1033-1043 header +
// layer-item render ~5707). The live panel is a fixed header row ("LAYERS"
// uppercase + collapse toggle) above a scrollable #layersList of .layer-item
// rows. Each row = type glyph + truncated name + click-to-select.
//
// Untangling (map §3.3 #4): the legacy markup wired clicks via injected
// onclick="selectLayer(i)" strings; here we build DOM nodes + addEventListener.
// We KEEP .layer-item / .layer-item.active — tests assert on them.
import type { FabricObject } from 'fabric'
import { icon } from '../ui/icons'
import { getCanvas, state } from './../state'

function layerLabel(obj: FabricObject): { glyph: string; name: string } {
  const o = obj as any
  const id: string = o.id || ''
  if (obj.type?.toLowerCase() === 'dynamictable') return { glyph: '▦', name: `Dynamic Table ${o.rows ?? ''}×${o.cols ?? ''}`.trim() }
  if (obj.type === 'Table' || o.isTable) return { glyph: '▦', name: `Table ${o.rows ?? ''}×${o.cols ?? ''}`.trim() }
  switch (obj.type) {
    case 'i-text': case 'text': case 'textbox':
      return { glyph: 'T', name: (o.text || 'Text').substring(0, 20) }
    case 'rect':
      return { glyph: '▢', name: o.rx > 0 ? 'Rounded Rect' : 'Rectangle' }
    case 'circle': return { glyph: '○', name: 'Circle' }
    case 'ellipse': return { glyph: '⬭', name: 'Ellipse' }
    case 'triangle': return { glyph: '△', name: 'Triangle' }
    case 'line': return { glyph: '—', name: 'Line' }
    case 'image': return { glyph: '▣', name: o.qrValue != null ? 'QR Code' : 'Image' }
    case 'path': {
      const map: Record<string, [string, string]> = {
        heart: ['♥', 'Heart'], check: ['✓', 'Checkmark'], xmark: ['✕', 'X Mark'],
        cloud: ['☁', 'Cloud'], bubble: ['💬', 'Speech Bubble'],
      }
      const k = Object.keys(map).find((p) => id.startsWith(p))
      return { glyph: k ? map[k][0] : '⟡', name: k ? map[k][1] : 'Path' }
    }
    case 'polygon': {
      const map: Record<string, [string, string]> = {
        star4: ['✦', '4-Point Star'], star: ['★', 'Star'], hex: ['⬡', 'Hexagon'],
        pent: ['⬠', 'Pentagon'], oct: ['⯃', 'Octagon'], diamond: ['◇', 'Diamond'],
        arrow: ['→', 'Arrow'], cross: ['+', 'Cross'],
      }
      const k = Object.keys(map).find((p) => id.startsWith(p))
      return { glyph: k ? map[k][0] : '⬠', name: k ? map[k][1] : 'Polygon' }
    }
    case 'group':
      if (o.barcodeFormat) return { glyph: '▥', name: 'Barcode' }
      if (id.startsWith('badge')) return { glyph: '◎', name: 'Badge' }
      if (id.startsWith('warn')) return { glyph: '⚠', name: 'Warning' }
      return { glyph: '▦', name: `Group (${o.getObjects?.().length ?? 0})` }
    default:
      return { glyph: '▢', name: 'Object' }
  }
}

/** Build (once) the fixed header row + scrollable list container into #layersPanel. */
function ensureScaffold(panel: HTMLElement): HTMLElement {
  let list = panel.querySelector<HTMLElement>('#layersList')
  if (list) return list

  panel.replaceChildren()

  // Header row: "LAYERS" uppercase + collapse toggle (live L1034-1039).
  const header = document.createElement('div')
  header.className = 'flex items-center justify-between px-3 h-9 border-b border-slate-800 shrink-0'
  const title = document.createElement('span')
  title.className = 'text-xs font-semibold text-slate-400 uppercase tracking-wider'
  title.textContent = 'Layers'
  const toggle = document.createElement('button')
  toggle.className = 'text-slate-500 hover:text-slate-300 transition-colors'
  toggle.title = 'Toggle Layers'
  toggle.innerHTML = icon('layersCollapse')
  toggle.addEventListener('click', () => panel.classList.toggle('layers-collapsed'))
  header.append(title, toggle)

  list = document.createElement('div')
  list.id = 'layersList'
  list.className = 'flex-1 overflow-y-auto p-2 space-y-1'

  panel.append(header, list)
  return list
}

export function updateLayers(): void {
  const panel = document.getElementById('layersPanel')
  if (!panel) return
  const list = ensureScaffold(panel)

  const canvas = getCanvas()
  const objects = canvas.getObjects().filter((o) => !(o as any).isGrid && !(o as any).isBgImage)
  list.replaceChildren()

  if (objects.length === 0) {
    const empty = document.createElement('div')
    empty.className = 'text-xs text-slate-500 text-center py-8'
    empty.innerHTML = 'No objects yet.<br>Add text, shapes, or images.'
    list.appendChild(empty)
    return
  }

  const active = canvas.getActiveObject()
  // Top layer first (reverse paint order) — matches live list ordering.
  objects.slice().reverse().forEach((obj) => {
    const { glyph, name } = layerLabel(obj)
    const row = document.createElement('div')
    row.className = 'layer-item' + (obj === active ? ' active' : '')

    const ic = document.createElement('span')
    ic.className = 'text-slate-400 w-4 text-center text-xs'
    ic.textContent = glyph

    const nm = document.createElement('span')
    nm.className = 'truncate flex-1 text-xs'
    nm.textContent = name
    nm.title = name

    row.append(ic, nm)
    row.addEventListener('click', () => selectLayer(obj))
    list.appendChild(row)
  })
}

export function selectLayer(obj: FabricObject): void {
  const canvas = getCanvas()
  canvas.setActiveObject(obj)
  canvas.requestRenderAll()
  void state
}
